import React, { useState, useEffect } from 'react';
import { Sun, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

function KolikoDanaDoLjeta() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [summerDate, setSummerDate] = useState(new Date());
  
  // Calculate summer start date (June 21st of current year)
  useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();
    const summerStart = new Date(year, 5, 21); // June 21st (month is 0-indexed)
    
    // If summer has already started this year, set to next year
    if (now > summerStart) {
      summerStart.setFullYear(year + 1);
    }
    
    setSummerDate(summerStart);
    setCurrentYear(now.getFullYear());
  }, []);
  
  // Update countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const difference = summerDate.getTime() - now.getTime();
      
      if (difference <= 0) {
        clearInterval(timer);
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        });
        return;
      }
      
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      
      setTimeLeft({ days, hours, minutes, seconds });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [summerDate]);
  
  // Format number with leading zero
  const formatNumber = (num: number): string => {
    return num.toString().padStart(2, '0');
  };
  
  // Calculate percentage of time passed until summer
  const calculateProgress = (): number => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    // Start from either last summer or beginning of year
    const startDate = new Date(currentYear, 0, 1); // January 1st
    const lastSummer = new Date(currentYear, 5, 21);
    
    if (now > lastSummer) {
      startDate.setTime(lastSummer.getTime());
    }
    
    const totalTime = summerDate.getTime() - startDate.getTime();
    const elapsedTime = now.getTime() - startDate.getTime();
    
    return Math.min(100, Math.max(0, (elapsedTime / totalTime) * 100));
  };
  
  // Get season information
  const getCurrentSeason = (): string => {
    const now = new Date();
    const month = now.getMonth();
    
    if (month >= 2 && month < 5) return 'proljeće';
    if (month >= 5 && month < 8) return 'ljeto';
    if (month >= 8 && month < 11) return 'jesen';
    return 'zima';
  };
  
  const getSeasonEmoji = (season: string): string => {
    switch (season) {
      case 'proljeće': return '🌱';
      case 'ljeto': return '☀️';
      case 'jesen': return '🍂';
      case 'zima': return '❄️';
      default: return '';
    }
  };
  
  const season = getCurrentSeason();
  const progress = calculateProgress();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Koliko Dana Do Ljeta",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Precizno odbrojavanje do početka ljeta. Saznajte točno koliko dana, sati, minuta i sekundi je ostalo do ljeta.",
    "url": "https://online-kalkulator.com/online-stoperica/koliko-dana-do-ljeta",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Precizno odbrojavanje do ljeta",
      "Prikaz dana, sati, minuta i sekundi",
      "Vizualni prikaz napretka",
      "Informacije o godišnjim dobima",
      "Automatsko ažuriranje"
    ]
  };

  return (
    <>
      <SEO 
        title="Koliko Dana Do Ljeta | Odbrojavanje Do Ljeta"
        description="Precizno odbrojavanje do početka ljeta. Saznajte točno koliko dana, sati, minuta i sekundi je ostalo do ljeta."
        canonicalUrl="/online-stoperica/koliko-dana-do-ljeta"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Sun className="w-8 h-8 text-yellow-500" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Koliko Dana Do Ljeta</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Countdown Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Season Info */}
              <div className="text-center mb-4">
                <div className="inline-block px-4 py-2 bg-blue-50 rounded-full">
                  <span className="text-lg font-medium text-blue-700">
                    Trenutno godišnje doba: <span className="font-bold">{season} {getSeasonEmoji(season)}</span>
                  </span>
                </div>
              </div>
              
              {/* Target Date */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-gray-600 mb-2">
                  <Calendar className="w-5 h-5" />
                  <span>Ljeto počinje: {summerDate.toLocaleDateString('hr-HR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-4 mb-6">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 via-green-500 to-yellow-500 rounded-full transition-all duration-1000"
                  style={{ width: `${progress}%` }}
                />
              </div>

              {/* Countdown Display */}
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-4xl font-bold text-blue-600 font-mono">
                    {timeLeft.days}
                  </div>
                  <div className="text-gray-600 text-sm mt-1">Dana</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-4xl font-bold text-blue-600 font-mono">
                    {formatNumber(timeLeft.hours)}
                  </div>
                  <div className="text-gray-600 text-sm mt-1">Sati</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-4xl font-bold text-blue-600 font-mono">
                    {formatNumber(timeLeft.minutes)}
                  </div>
                  <div className="text-gray-600 text-sm mt-1">Minuta</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-4xl font-bold text-blue-600 font-mono">
                    {formatNumber(timeLeft.seconds)}
                  </div>
                  <div className="text-gray-600 text-sm mt-1">Sekundi</div>
                </div>
              </div>
              
              {/* Summer Message */}
              {season === 'ljeto' && (
                <div className="text-center py-3 px-4 bg-yellow-50 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <Sun className="w-5 h-5 text-yellow-500" />
                    <p className="text-lg font-medium text-yellow-700">Ljeto je već počelo! Uživajte u suncu i toplim danima!</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Odbrojavanju Do Ljeta</h2>
                <p className="text-gray-600 leading-relaxed">
                  Odbrojavanje do ljeta je zabavan način da pratite koliko je vremena preostalo do
                  službenog početka ljeta. Ljeto astronomski počinje na ljetni suncostaj, koji pada
                  oko 21. lipnja na sjevernoj hemisferi. Ovaj datum označava najduži dan u godini i
                  službeni početak ljetne sezone.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Godišnja Doba</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🌱</span>
                      <h3 className="text-lg font-medium text-green-700">Proljeće</h3>
                    </div>
                    <p className="text-gray-600">
                      Počinje oko 20. ožujka i traje do 21. lipnja. Karakteriziraju ga umjerene temperature
                      i buđenje prirode nakon zime.
                    </p>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">☀️</span>
                      <h3 className="text-lg font-medium text-yellow-700">Ljeto</h3>
                    </div>
                    <p className="text-gray-600">
                      Počinje oko 21. lipnja i traje do 23. rujna. Najduži dani u godini, visoke
                      temperature i vrijeme za odmor i uživanje na otvorenom.
                    </p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🍂</span>
                      <h3 className="text-lg font-medium text-orange-700">Jesen</h3>
                    </div>
                    <p className="text-gray-600">
                      Počinje oko 23. rujna i traje do 21. prosinca. Karakteriziraju je promjene boja
                      lišća, umjerene temperature i berba plodova.
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">❄️</span>
                      <h3 className="text-lg font-medium text-blue-700">Zima</h3>
                    </div>
                    <p className="text-gray-600">
                      Počinje oko 21. prosinca i traje do 20. ožujka. Najkraći dani u godini, niske
                      temperature i često snijeg u kontinentalnim područjima.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Zanimljivosti o Ljetu</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Ljetni suncostaj je najduži dan u godini na sjevernoj hemisferi</li>
                  <li>Na južnoj hemisferi, ljeto počinje oko 21. prosinca</li>
                  <li>Tijekom ljeta, Zemlja nije najbliža Suncu - zapravo je najdalja početkom srpnja</li>
                  <li>Ljeto je toplije zbog kuta pod kojim Sunčeve zrake padaju na Zemlju</li>
                  <li>U nekim zemljama, ljeto se računa meteorološki od 1. lipnja do 31. kolovoza</li>
                  <li>Najduži dan ljeta može imati i do 16 sati dnevnog svjetla u Hrvatskoj</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti Odbrojavanje</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Odbrojavanje se automatski ažurira svake sekunde</li>
                  <li>Prikazuje točan broj dana, sati, minuta i sekundi do početka ljeta</li>
                  <li>Traka napretka vizualno prikazuje koliko je vremena prošlo od zadnjeg ljeta ili početka godine</li>
                  <li>Ako je trenutno ljeto, odbrojavanje će pokazivati vrijeme do sljedećeg ljeta</li>
                  <li>Stranica se automatski ažurira svake godine za novo odbrojavanje</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Planiranje Ljetnih Aktivnosti</h2>
                <div className="space-y-3">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-1">Putovanja i odmor</h3>
                    <p className="text-gray-600 text-sm">
                      Iskoristite odbrojavanje za planiranje godišnjeg odmora, rezervacije smještaja i organizaciju putovanja.
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-1">Ljetni projekti</h3>
                    <p className="text-gray-600 text-sm">
                      Planirajte ljetne projekte, uređenje vrta, renovacije ili hobije za koje imate više vremena tijekom ljeta.
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-1">Fizička priprema</h3>
                    <p className="text-gray-600 text-sm">
                      Koristite preostalo vrijeme do ljeta za postizanje željene fizičke forme i pripreme za ljetne aktivnosti.
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-1">Događaji i festivali</h3>
                    <p className="text-gray-600 text-sm">
                      Istražite i planirajte posjete ljetnim festivalima, koncertima i događajima koji vas zanimaju.
                    </p>
                  </div>
                </div>
              </section>

              {/* Interlinking Section */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Ostala Odbrojavanja</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Link to="/online-stoperica/koliko-dana-do-jeseni" className="p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🍂</span>
                      <h3 className="text-lg font-medium text-orange-700">Koliko Dana Do Jeseni</h3>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Saznajte koliko je dana ostalo do početka jeseni.
                    </p>
                  </Link>
                  <Link to="/online-stoperica/koliko-dana-do-zime" className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">❄️</span>
                      <h3 className="text-lg font-medium text-blue-700">Koliko Dana Do Zime</h3>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Saznajte koliko je dana ostalo do početka zime.
                    </p>
                  </Link>
                  <Link to="/online-stoperica/koliko-dana-do-proljeca" className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🌱</span>
                      <h3 className="text-lg font-medium text-green-700">Koliko Dana Do Proljeća</h3>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Saznajte koliko je dana ostalo do početka proljeća.
                    </p>
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KolikoDanaDoLjeta;